import React, { memo, useState } from "react"
import type { Enrollment, Group, Submission } from "../../../proto/qf/types_pb"
import { GradingCriterion_Grade } from "../../../proto/qf/types_pb"
import { getSubmissionCellColor } from "../../Helpers"
import { useAppState } from "../../overmind"

/** Represents both possible submissions for a group lab assignment */
interface SubmissionPair {
    individual?: Submission
    group?: Submission
}

interface SubmissionCellProps {
    submissionPair: SubmissionPair
    owner: Enrollment | Group
    onSubmissionClick: (submission: Submission) => void
    review: boolean
}

const SubmissionCell = memo(({ submissionPair, owner, onSubmissionClick, review }: SubmissionCellProps) => {
    const state = useAppState()

    // Check if submissions actually exist (not just truthy, but valid submission objects)
    const hasIndividual = submissionPair.individual !== undefined && submissionPair.individual !== null
    const hasGroup = submissionPair.group !== undefined && submissionPair.group !== null
    const hasBothSubmissions = hasIndividual && hasGroup

    // Local state to toggle between individual and group submission when both exist
    // Only used when both submissions are available
    const [showGroup, setShowGroup] = useState(!state.individualSubmissionView)

    // Determine which submission to display and whether it's a group submission.
    // When both exist, the toggle state (showGroup) decides which one is shown.
    // When only one exists, we show that one unconditionally.
    let isGroupSubmission: boolean
    let submission: Submission | undefined
    if (hasBothSubmissions) {
        isGroupSubmission = showGroup
        submission = showGroup ? submissionPair.group : submissionPair.individual
    } else if (hasGroup) {
        isGroupSubmission = true
        submission = submissionPair.group
    } else {
        isGroupSubmission = false
        submission = submissionPair.individual
    }

    // Guard: this should never happen since MemberRow only renders a cell when at least one submission exists
    if (!submission) { return null }

    const isSelected = state.selectedSubmission?.ID === submission.ID
    const colorClass = getSubmissionCellColor(submission, owner)
    // Apply a primary ring highlight when this submission is currently selected in the panel
    const selectedClass = isSelected ? "ring-2 ring-primary ring-inset" : ""

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowGroup(!showGroup)
    }

    if (review) {
        return (
            <ReviewCell
                submission={submission}
                onClick={() => onSubmissionClick(submission)}
                isSelected={isSelected}
                colorClass={colorClass}
            />
        )
    }

    // Determine what indicator to show:
    // - Both exist: clickable toggle button
    // - Only group exists: static group icon (for enrollments viewing group lab)
    // - Only individual exists: no icon needed (it's the default/expected case)
    const renderIndicator = () => {
        if (hasBothSubmissions) {
            return (
                <button
                    onClick={handleToggle}
                    className="btn btn-xs btn-ghost px-1 min-h-0 h-5"
                    title={isGroupSubmission ? "Showing group submission (click for individual)" : "Showing individual submission (click for group)"}
                >
                    <i className={isGroupSubmission ? "fas fa-users" : "fas fa-user"} />
                </button>
            )
        }
        if (hasGroup && !hasIndividual) {
            // Only show group icon when it's a group submission without an individual alternative
            return <i className="fas fa-users text-xs opacity-60" title="Group submission" />
        }
        return null
    }

    return (
        <td
            className={`cursor-pointer ${colorClass} ${selectedClass}`}
            onClick={() => onSubmissionClick(submission)}
        >
            <div className="flex items-center justify-left gap-1">
                <span>{submission.score}</span>
                {renderIndicator()}
            </div>
        </td>
    )
})

SubmissionCell.displayName = "SubmissionCell"

interface ReviewCellProps {
    submission: Submission
    onClick: () => void
    isSelected: boolean
    colorClass: string
}

const ReviewCell = memo(({ submission, onClick, isSelected, colorClass }: ReviewCellProps) => {
    const state = useAppState()

    if (!state.isManuallyGraded(submission)) {
        return (
            <td className="text-base-content/50 text-center" title="Auto graded">
                —
            </td>
        )
    }

    const reviews = state.review.reviews.get(submission.ID) ?? []
    const numReviewers = state.assignments[state.activeCourse.toString()]?.find(
        a => a.ID === submission.AssignmentID
    )?.reviewers ?? 0

    // Check if the current user has any pending reviews for this submission
    const hasPendingReview = reviews.some(r =>
        r.gradingBenchmarks.some(benchmark =>
            benchmark.criteria.some(criterion => criterion.grade === GradingCriterion_Grade.NONE)
        ) && r.ReviewerID === state.self.ID
    )

    const selectedClass = isSelected ? "ring-2 ring-primary ring-inset" : ""
    const pendingClass = hasPendingReview ? "shadow-[inset_0_0_0_4px_rgba(255,140,0,0.8)]" : ""

    return (
        <td
            className={`cursor-pointer ${colorClass} ${selectedClass} ${pendingClass}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-center gap-1">
                <span>{reviews.length}/{numReviewers}</span>
            </div>
        </td>
    )
})

ReviewCell.displayName = "ReviewCell"

export default SubmissionCell
